import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useBranch } from '../context/BranchContext';
import { 
  Upload, 
  Plus, 
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  Package, 
  AlertCircle,
  Search,
  AlertTriangle,
  Calendar,
  Clock,
  Warehouse,
  ClipboardList,
  FileCheck,
  FileText,
  FileQuestion,
  CheckCircle,
  Check,
  Copy,
  MoreVertical,
  ExternalLink,
  Wrench,
  User,
  Send,
  Printer,
  TrendingUp,
  UserPlus,
  Sparkles,
  X as XIcon
} from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import AppHeader from '../components/AppHeader';
import PICountSchedule from '../components/PICountSchedule';
import CreateReportModal from '../components/reports/CreateReportModal';
import SavedReportsModal from '../components/reports/SavedReportsModal';
import ToolsDropdown from '../components/ToolsDropdown';

// Safe date formatting helper to prevent white screen crashes
const safeFormatDate = (dateValue, formatStr, fallback = '-') => {
  if (!dateValue) return fallback;
  try {
    const date = typeof dateValue === 'string' ? parseISO(dateValue) : new Date(dateValue);
    if (!isValid(date)) return fallback;
    return format(date, formatStr);
  } catch (e) {
    console.warn('Date formatting error:', e, dateValue);
    return fallback;
  }
};

const PICounts = ({ user }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Branch context for data isolation
  const { getEffectiveBranchId } = useBranch();
  const effectiveBranchId = getEffectiveBranchId();
  
  // Color constants
  const enconOrange = '#ee7203';
  const enconLightGrey = '#e2e8f0';
  const enconBlack = '#1a202c';
  
  // State
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [incompleteCounts, setIncompleteCounts] = useState([]);
  const [monthCounts, setMonthCounts] = useState([]);
  const [showUploadStockModal, setShowUploadStockModal] = useState(false);
  const [showCreateCountModal, setShowCreateCountModal] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stockUploadEmailCopied, setStockUploadEmailCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [supplierGroups, setSupplierGroups] = useState([]);
  const [inventorySearchQuery, setInventorySearchQuery] = useState('');
  const [inventoryResults, setInventoryResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  
  // Quick Links state
  const [quickLinks, setQuickLinks] = useState([]);
  const [linkGroups, setLinkGroups] = useState([]);
  const [linksDropdownOpen, setLinksDropdownOpen] = useState(false);
  
  // Create Count State
  const [newCount, setNewCount] = useState({
    type: 'structural',
    supplier_group_ids: [],
    date: format(new Date(), 'yyyy-MM-dd')
  });
  const [supplierSearchQuery, setSupplierSearchQuery] = useState('');
  const [selectiveMode, setSelectiveMode] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProductCodes, setSelectedProductCodes] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  // Shelf Life Products mode — seeds the product selection panel from the
  // fixed Shelf Life / Date-Sensitive register instead of supplier groups,
  // and lets the user manually add extra lines not on the register.
  const [shelfLifeMode, setShelfLifeMode] = useState(false);
  const [shelfLifeAddQuery, setShelfLifeAddQuery] = useState('');
  const [shelfLifeAddResults, setShelfLifeAddResults] = useState([]);
  const [shelfLifeAddTimeout, setShelfLifeAddTimeout] = useState(null);
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const [returnedFromWarehouse, setReturnedFromWarehouse] = useState([]);
  const [discrepancyReports, setDiscrepancyReports] = useState([]);
  const [discrepancyReportsAtWarehouse, setDiscrepancyReportsAtWarehouse] = useState([]);
  const [showDiscrepanciesSection, setShowDiscrepanciesSection] = useState(false);
  // Scheduled counts (new feature replacing the old red "Discrepancies" pill)
  const [scheduledCounts, setScheduledCounts] = useState([]);
  const [showScheduleSection, setShowScheduleSection] = useState(false);
  const [pendingAtWarehouse, setPendingAtWarehouse] = useState([]);
  const [showAdjustmentsSection, setShowAdjustmentsSection] = useState(false);
  const [pendingAdjustments, setPendingAdjustments] = useState([]);
  const [completedAdjustments, setCompletedAdjustments] = useState([]);
  const [adjustmentSearchQuery, setAdjustmentSearchQuery] = useState('');
  const [showSentToWarehouseSection, setShowSentToWarehouseSection] = useState(false);
  // Action Required is now a tab too. Auto-opens when it has items;
  // collapses otherwise so the month view sits directly under the tabs.
  const [showActionSection, setShowActionSection] = useState(false);
  // Create-Report + Saved-Reports modals
  const [showCreateReportModal, setShowCreateReportModal] = useState(false);
  const [showSavedReportsModal, setShowSavedReportsModal] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [showCompletedAdjustmentsModal, setShowCompletedAdjustmentsModal] = useState(false);
  const [adjustmentMenuOpen, setAdjustmentMenuOpen] = useState(null); // Track which adjustment menu is open
  const [summaryCounts, setSummaryCounts] = useState(null); // Summary counts for restricted view
  const [quickMenuCountId, setQuickMenuCountId] = useState(null); // Quick action menu for count cards
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigneeName, setAssigneeName] = useState('');
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [assignCountId, setAssignCountId] = useState(null);
  const [sendingToWarehouse, setSendingToWarehouse] = useState(false);
  const [teamMembers, setTeamMembers] = useState({});
  const [selectedTeamFilter, setSelectedTeamFilter] = useState('default'); // 'default' shows warehouse+ops
  const [showNewAccountsModal, setShowNewAccountsModal] = useState(false);
  
  // Check if user has restricted visibility
  const isVisibilityRestricted = user?.pi_counts_visibility_restricted || false;
  
  // Auto-refresh discrepancy reports on page load (runs once)
  useEffect(() => {
    const refreshDiscrepancyExpectedQuantities = async () => {
      try {
        await api.post(`${API}/pi-counts/discrepancy/refresh-expected-quantities`);
        console.log('✅ Auto-refreshed discrepancy expected quantities');
      } catch (error) {
        console.error('Error auto-refreshing discrepancy quantities:', error);
      }
    };
    
    // Only refresh if user has full access (not restricted)
    if (!isVisibilityRestricted) {
      refreshDiscrepancyExpectedQuantities();
    }
  }, []); // Empty dependency array - runs once on mount
  
  useEffect(() => {
    // Load summary counts for all users (needed for badges)
    loadSummaryCounts();
    
    // Only load full data if not restricted
    if (!isVisibilityRestricted) {
      loadIncompleteCounts();
      loadReturnedFromWarehouse();
      loadDiscrepancyReports();
      loadDiscrepancyReportsAtWarehouse();
      loadScheduledCounts();
      loadPendingAtWarehouse();
      loadAdjustmentReports();
    }
    loadSupplierGroups();
    loadMonthCounts();
    loadQuickLinks();
  }, [currentYear, selectedMonth, isVisibilityRestricted, effectiveBranchId]);
  
  // Close quick menu when clicking outside
  useEffect(() => {
    if (!quickMenuCountId) return;
    const handleClick = () => setQuickMenuCountId(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [quickMenuCountId]);

  // Deep-link from the orange PI Counts notification bar:
  //   /pi-counts?action=send-to-wh&countId=<id>
  // Triggers the "Send to Warehouse" assignee modal for the targeted count
  // exactly as if the user had clicked the count's quick-menu on this page.
  // The query params are cleared once consumed so a refresh doesn't reopen
  // the modal indefinitely.
  //
  // Also handles ?open=warehouse — auto-expands the Warehouse section and
  // scrolls to it. Used when navigating back from PICountDetail after a
  // successful warehouse send so the user immediately sees the count.
  useEffect(() => {
    const action = searchParams.get('action');
    const countId = searchParams.get('countId');
    const open = searchParams.get('open');

    if (action === 'send-to-wh' && countId) {
      handleQuickMenuSendToWarehouse(countId);
      const next = new URLSearchParams(searchParams);
      next.delete('action');
      next.delete('countId');
      setSearchParams(next, { replace: true });
    }

    if (open === 'warehouse') {
      (async () => {
        await loadPendingAtWarehouse();
        setShowSentToWarehouseSection(true);
        setShowActionSection(false);
        setShowDiscrepanciesSection(false);
        setShowAdjustmentsSection(false);
        setShowScheduleSection(false);
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
      })();
      const next = new URLSearchParams(searchParams);
      next.delete('open');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]);

  // Load Quick Links
  const loadQuickLinks = async () => {
    try {
      const response = await api.get(`${API}/quick-links`);
      setQuickLinks(response.data);
      
      // Load link groups
      const groupsResponse = await api.get(`${API}/quick-link-groups`);
      setLinkGroups(groupsResponse.data.sort((a, b) => a.order - b.order));
    } catch (error) {
      console.error('Error loading quick links:', error);
    }
  };
  
  const getLinksForGroup = (groupId) => {
    return quickLinks.filter(link => (link.group_id || 'default') === groupId).sort((a, b) => a.order - b.order);
  };
  
  const handleQuickLinkClick = (link) => {
    if (link.url.startsWith('http://') || link.url.startsWith('https://')) {
      window.open(link.url, '_blank');
    } else {
      navigate(link.url);
    }
  };
  
  const loadSummaryCounts = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/summary-counts`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setSummaryCounts(response.data);
    } catch (error) {
      console.error('Error loading summary counts:', error);
    }
  };

  // Global drag and drop for CSV upload
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(true);
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      // Only set to false if leaving the window
      if (e.relatedTarget === null) {
        setIsDragging(false);
      }
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleFileDrop(files[0]);
      }
    };
    
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, []);

  const handleFileDrop = async (file) => {
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    setShowUploadStockModal(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`${API}/pi-counts/upload-stock`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success(`Stock valuation uploaded: ${response.data.products_count} products`);
      setShowUploadStockModal(false);
      // Refresh supplier groups so Create Count immediately shows updated list
      await loadSupplierGroups();
    } catch (error) {
      console.error('Error uploading stock:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  
  const loadPendingAtWarehouse = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/warehouse`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setPendingAtWarehouse(response.data.counts || []);
    } catch (error) {
      console.error('Error loading counts at warehouse:', error);
    }
  };
  
  const loadIncompleteCounts = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/incomplete`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setIncompleteCounts(response.data.counts || []);
    } catch (error) {
      console.error('Error loading incomplete counts:', error);
    }
  };
  
  const loadReturnedFromWarehouse = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/returned-from-warehouse`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setReturnedFromWarehouse(response.data.counts || []);
    } catch (error) {
      console.error('Error loading returned counts:', error);
    }
  };
  
  const loadDiscrepancyReports = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/discrepancy-reports`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setDiscrepancyReports(response.data.reports || []);
    } catch (error) {
      console.error('Error loading discrepancy reports:', error);
    }
  };

  const loadDiscrepancyReportsAtWarehouse = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/discrepancy-reports-at-warehouse`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setDiscrepancyReportsAtWarehouse(response.data.reports || []);
    } catch (error) {
      console.error('Error loading discrepancy reports at warehouse:', error);
    }
  };

  // Scheduled counts loader — any pi_counts with status='scheduled' grouped by date.
  const loadScheduledCounts = async () => {
    try {
      const response = await api.get(`${API}/pi-counts/scheduled`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setScheduledCounts(response.data.counts || response.data || []);
    } catch (error) {
      // Fall back to client-side filter of the month window
      try {
        const all = monthCounts || [];
        setScheduledCounts(all.filter(c => c.status === 'scheduled'));
      } catch (_) { setScheduledCounts([]); }
    }
  };

  const loadAdjustmentReports = async () => {
    try {
      const [pendingRes, completedRes] = await Promise.all([
        api.get(`${API}/pi-counts/adjustment-reports/pending`, {
          params: { view_as_branch: effectiveBranchId }
        }),
        api.get(`${API}/pi-counts/adjustment-reports/completed`, {
          params: { 
            view_as_branch: effectiveBranchId,
            ...(adjustmentSearchQuery && { search: adjustmentSearchQuery })
          }
        })
      ]);
      setPendingAdjustments(pendingRes.data.reports || []);
      setCompletedAdjustments(completedRes.data.reports || []);
    } catch (error) {
      console.error('Error loading adjustment reports:', error);
    }
  };

  // Search adjustments when query changes
  useEffect(() => {
    if (showAdjustmentsSection) {
      const timer = setTimeout(() => {
        loadAdjustmentReports();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [adjustmentSearchQuery]);
  
  // Handler to send pending adjustment back to discrepancy
  const handleSendToDiscrepancy = async (adjustmentId, e) => {
    e.stopPropagation(); // Prevent card click
    setAdjustmentMenuOpen(null);
    
    try {
      const token = localStorage.getItem('token');
      await api.post(`${API}/pi-counts/adjustment-reports/${adjustmentId}/send-to-discrepancy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Adjustment sent back to discrepancy');
      loadAdjustmentReports();
      loadDiscrepancyReports();
    } catch (error) {
      console.error('Error sending to discrepancy:', error);
      toast.error('Failed to send adjustment to discrepancy');
    }
  };
  
  // Handler to send pending adjustment back to recount
  const handleSendToRecount = async (adjustmentId, e) => {
    e.stopPropagation(); // Prevent card click
    setAdjustmentMenuOpen(null);
    
    try {
      const token = localStorage.getItem('token');
      await api.post(`${API}/pi-counts/adjustment-reports/${adjustmentId}/send-to-second-count`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Adjustment sent for recount');
      loadAdjustmentReports();
      loadPendingAtWarehouse();
    } catch (error) {
      console.error('Error sending to recount:', error);
      toast.error('Failed to send adjustment for recount');
    }
  };
  
  // Quick menu handlers for action required cards
  const handleQuickMenuSendToWarehouse = (countId) => {
    setQuickMenuCountId(null);
    setAssignCountId(countId);
    setAssigneeName('');
    setAssigneeEmail('');
    setSelectedTeamFilter('default');
    setShowAssignModal(true);
    loadTeamMembers();
  };

  const loadTeamMembers = async () => {
    try {
      const resp = await api.get(`${API}/teams/members`, {
        params: { branch_id: effectiveBranchId }
      });
      setTeamMembers(resp.data.teams || {});
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  // Get filtered team members for the assign dropdown
  const getFilteredTeamMembers = () => {
    if (selectedTeamFilter === 'default') {
      // Show Warehouse + Operations by default
      return [
        ...(teamMembers['Warehouse'] || []),
        ...(teamMembers['Operations'] || [])
      ];
    }
    if (selectedTeamFilter === 'all') {
      return Object.values(teamMembers).flat();
    }
    const teamLabel = selectedTeamFilter === 'warehouse' ? 'Warehouse' :
                      selectedTeamFilter === 'operations' ? 'Operations' :
                      selectedTeamFilter === 'office_sales' ? 'Office Sales' :
                      selectedTeamFilter === 'admin' ? 'Admin' : selectedTeamFilter;
    return teamMembers[teamLabel] || [];
  };

  const confirmSendToWarehouse = async () => {
    if (!assignCountId) return;
    setShowAssignModal(false);
    setSendingToWarehouse(true);
    try {
      const token = localStorage.getItem('token');
      await api.put(`${API}/pi-counts/${assignCountId}/send-to-warehouse`, 
        { assigned_to: assigneeName.trim(), assigned_email: assigneeEmail.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Count sent to Warehouse');
      // Refresh lists — await warehouse data so the section opens with counts
      // already populated (prevents the brief "No counts at warehouse" flicker).
      loadIncompleteCounts();
      loadScheduledCounts();
      await loadPendingAtWarehouse();
      loadMonthCounts();
      window.dispatchEvent(new Event('c30:pi-counts-changed'));
      setShowSentToWarehouseSection(true);
      setShowActionSection(false);
      setShowDiscrepanciesSection(false);
      setShowAdjustmentsSection(false);
      setShowScheduleSection(false);
    } catch (error) {
      console.error('Error sending to warehouse:', error);
      toast.error(error.response?.data?.detail || 'Failed to send to warehouse');
    } finally {
      setSendingToWarehouse(false);
      setAssignCountId(null);
    }
  };

  const handleQuickMenuPrint = (countId) => {
    setQuickMenuCountId(null);
    navigate(`/pi-counts/${countId}?action=print`);
  };

  // Get Monday of the week for a given date
  const getWeekCommencing = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.setDate(diff));
  };
  
  const loadMonthCounts = async () => {
    try {
      const monthStr = `${currentYear}-${String(selectedMonth + 1).padStart(2, '0')}`;
      const response = await api.get(`${API}/pi-counts/month/${monthStr}`, {
        params: { view_as_branch: effectiveBranchId }
      });
      setMonthCounts(response.data.counts || []);
    } catch (error) {
      console.error('Error loading month counts:', error);
    }
  };
  
  const loadSupplierGroups = async () => {
    try {
      // Get manual supplier groups (the new grouping of Product Analysis Groups)
      const response = await api.get(`${API}/pi-counts/available-supplier-groups`);
      setSupplierGroups(response.data.suppliers || []);
    } catch (error) {
      console.error('Error loading supplier groups:', error);
    }
  };
  
  const handleStockUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`${API}/pi-counts/upload-stock`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });
      
      toast.success(`Stock valuation uploaded: ${response.data.products_count} products`);
      setShowUploadStockModal(false);
    } catch (error) {
      console.error('Error uploading stock:', error);
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      event.target.value = '';
    }
  };
  
  // Load products for selected supplier groups (for selective counting)
  const loadProductsForSelection = async () => {
    if (newCount.supplier_group_ids.length === 0) {
      toast.error('Please select at least one supplier group first');
      return;
    }
    setLoadingProducts(true);
    try {
      const response = await api.post(`${API}/pi-counts/preview-products`, {
        supplier_group_ids: newCount.supplier_group_ids
      });
      setAvailableProducts(response.data.products || []);
      setSelectedProductCodes([]);
      setProductSearchQuery('');
      setSelectiveMode(true);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load the fixed Shelf Life / Date-Sensitive register (enriched with live
  // on-hand from today's Free Stock Report) into the same product-selection
  // panel used for normal selective counts, with everything pre-ticked.
  const loadShelfLifeProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api.get(`${API}/pi-counts/shelf-life-products`);
      const products = response.data.products || [];
      setAvailableProducts(products);
      setSelectedProductCodes(products.map(p => p.product_code));
      setProductSearchQuery('');
      setSelectiveMode(true);
      setShelfLifeMode(true);
      setNewCount(prev => ({ ...prev, type: 'shelf_life', supplier_group_ids: [] }));
    } catch (error) {
      console.error('Error loading Shelf Life products:', error);
      toast.error('Failed to load Shelf Life products');
    } finally {
      setLoadingProducts(false);
    }
  };

  // Search the full stock database so the user can manually add a product
  // that isn't on the fixed Shelf Life register.
  const searchShelfLifeAdd = async (query) => {
    if (!query || !query.trim()) {
      setShelfLifeAddResults([]);
      return;
    }
    try {
      const response = await api.get(`${API}/stock-database/search`, { params: { query } });
      setShelfLifeAddResults(response.data.products || []);
    } catch (error) {
      console.error('Error searching products to add:', error);
    }
  };

  const handleShelfLifeAddQueryChange = (value) => {
    setShelfLifeAddQuery(value);
    if (shelfLifeAddTimeout) clearTimeout(shelfLifeAddTimeout);
    const newTimeout = setTimeout(() => searchShelfLifeAdd(value), 300);
    setShelfLifeAddTimeout(newTimeout);
  };

  const addShelfLifeProduct = (product) => {
    const code = (product.code || product.product_code || '').trim();
    if (!code) return;
    const codeUp = code.toUpperCase();
    if (selectedProductCodes.some(c => c.toUpperCase() === codeUp)) {
      toast.error('That product is already in the list');
      return;
    }
    setAvailableProducts(prev => [...prev, {
      product_code: code,
      product_name: product.name || product.description || product.product_name || '',
      not_in_stock: !!product.not_in_stock,
    }]);
    setSelectedProductCodes(prev => [...prev, code]);
    setShelfLifeAddQuery('');
    setShelfLifeAddResults([]);
    toast.success(`${code} added to the list`);
  };

  const toggleProductSelection = (code) => {
    setSelectedProductCodes(prev => 
      prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]
    );
  };

  const createCount = async () => {
    if (newCount.supplier_group_ids.length === 0) {
      toast.error('Please select at least one supplier group');
      return;
    }
    
    if (selectiveMode && selectedProductCodes.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    
    try {
      const payload = { ...newCount };
      if (selectiveMode && selectedProductCodes.length > 0) {
        payload.selected_product_codes = selectedProductCodes;
      }
      const response = await api.post(`${API}/pi-counts/create-multiple`, payload);
      toast.success(`Created ${response.data.counts.length} count sheet(s) successfully`);
      setShowCreateCountModal(false);
      setNewCount({ type: 'structural', supplier_group_ids: [], date: format(new Date(), 'yyyy-MM-dd') });
      setSupplierSearchQuery('');
      setSelectiveMode(false);
      setSelectedProductCodes([]);
      setAvailableProducts([]);
      await loadIncompleteCounts();
      await loadMonthCounts();
      
      // Stay on main page - the new count will appear in Action Required
    } catch (error) {
      console.error('Error creating count:', error);
      
      // Check if it's the free stock report missing error
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('No Free Stock Report')) {
        toast.error('No Stock Report uploaded today', {
          description: 'Please upload GFST0000.csv first',
          duration: 5000
        });
        // Open upload modal
        setShowCreateCountModal(false);
        setShowUploadStockModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create count');
      }
    }
  };

  // Create a Shelf Life count from the fixed register + any manually added
  // lines. Works the same as createCount but calls the dedicated
  // create-shelf-life endpoint (no supplier groups involved).
  const createShelfLifeCount = async () => {
    if (selectedProductCodes.length === 0) {
      toast.error('Please select at least one product');
      return;
    }
    try {
      const response = await api.post(`${API}/pi-counts/create-shelf-life`, {
        date: newCount.date,
        product_codes: selectedProductCodes
      });
      toast.success(`Created Shelf Life count with ${response.data.count.products_count} product(s)`);
      setShowCreateCountModal(false);
      setNewCount({ type: 'structural', supplier_group_ids: [], date: format(new Date(), 'yyyy-MM-dd') });
      setSupplierSearchQuery('');
      setSelectiveMode(false);
      setSelectedProductCodes([]);
      setAvailableProducts([]);
      setShelfLifeMode(false);
      setShelfLifeAddQuery('');
      setShelfLifeAddResults([]);
      await loadIncompleteCounts();
      await loadMonthCounts();
    } catch (error) {
      console.error('Error creating shelf life count:', error);
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('No Free Stock Report')) {
        toast.error('No Stock Report uploaded today', {
          description: 'Please upload GFST0000.csv first',
          duration: 5000
        });
        setShowCreateCountModal(false);
        setShowUploadStockModal(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create Shelf Life count');
      }
    }
  };
  
  // Group counts by date - filter for restricted users to only show completed counts
  // Auto-open / auto-close the Action Required tab based on whether there
  // are any items inside. Initial load: open if there are items, closed
  // otherwise. Subsequent changes: flip on the 0 ↔ >0 boundary so we don't
  // fight the user's explicit toggle.
  const actionItemCount = (returnedFromWarehouse?.length || 0) + (incompleteCounts?.length || 0);
  const prevActionCountRef = useRef(null);
  useEffect(() => {
    const prev = prevActionCountRef.current;
    if (prev === null) {
      // First evaluation after mount.
      setShowActionSection(actionItemCount > 0);
    } else {
      if (prev === 0 && actionItemCount > 0) setShowActionSection(true);
      if (prev > 0 && actionItemCount === 0) setShowActionSection(false);
    }
    prevActionCountRef.current = actionItemCount;
  }, [actionItemCount]);

  const filteredMonthCounts = isVisibilityRestricted 
    ? monthCounts.filter(count => count.status === 'completed')
    : monthCounts;
    
  const countsByDate = filteredMonthCounts.reduce((acc, count) => {
    const date = count.date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(count);
    return acc;
  }, {});
  
  // Sort dates in reverse order (most recent first, today at top)
  const sortedDates = Object.keys(countsByDate).sort((a, b) => new Date(b) - new Date(a));
  
  // Filter supplier groups by type
  const filteredSuppliers = supplierGroups
    .filter(s => s.category === newCount.type)
    .filter(s => {
      if (!supplierSearchQuery) return true;
      const q = supplierSearchQuery.toLowerCase().trim();
      // Search by supplier group name
      if (s.name.toLowerCase().includes(q)) return true;
      // Search by prefix codes (e.g. "30K", "30V")
      if (s.prefixes && s.prefixes.some(p => p.toLowerCase().includes(q))) return true;
      // Search by product codes
      if (s.product_codes && s.product_codes.some(c => c.toLowerCase().includes(q))) return true;
      return false;
    });
  
  const toggleSupplierSelection = (supplierId) => {
    setNewCount(prev => ({
      ...prev,
      supplier_group_ids: prev.supplier_group_ids.includes(supplierId)
        ? prev.supplier_group_ids.filter(id => id !== supplierId)
        : [...prev.supplier_group_ids, supplierId]
    }));
    // Reset selective mode when supplier groups change
    setSelectiveMode(false);
    setSelectedProductCodes([]);
    setAvailableProducts([]);
  };

  const searchInventory = async (query) => {
    if (!query || !query.trim()) {
      setInventoryResults([]);
      return;
    }
    
    try {
      const response = await api.get(`${API}/stock-database/search`, {
        params: { query: query }
      });
      setInventoryResults(response.data.products || []);
    } catch (error) {
      console.error('Error searching inventory:', error);
    }
  };
  
  const handleSearchChange = (value) => {
    setInventorySearchQuery(value);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for live search
    const newTimeout = setTimeout(() => {
      searchInventory(value);
    }, 300); // 300ms debounce
    
    setSearchTimeout(newTimeout);
  };
  
  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  // Page-specific buttons for the header - hide for restricted visibility
  // Note: Inventory button removed as global search in header provides the same functionality
  const subHeaderLeft = isVisibilityRestricted ? null : (
    <div className="flex items-center gap-1.5 rounded-full px-3 py-1 flex-shrink-0" style={{ background: 'rgba(0,0,0,0.22)' }}>
      <ClipboardList className="w-3.5 h-3.5 text-white/90 flex-shrink-0" />
      <span className="text-white font-bold text-xs sm:text-sm whitespace-nowrap">PI Counts</span>
    </div>
  );

  const subHeaderRight = isVisibilityRestricted ? null : (
    <>
      <button
        onClick={() => setShowUploadStockModal(true)}
        className="px-3 py-1.5 rounded-full font-semibold hover:shadow-md flex items-center gap-1.5 transition-all bg-white text-xs sm:text-sm"
        style={{ color: enconOrange }}
        title="Upload Stock"
        data-testid="pi-counts-subheader-upload-btn"
      >
        <Upload className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Upload</span>
      </button>
      <button
        onClick={() => setShowCreateCountModal(true)}
        className="px-3 py-1.5 rounded-full font-semibold hover:shadow-md flex items-center gap-1.5 transition-all bg-white/90 text-xs sm:text-sm"
        style={{ color: enconOrange }}
        title="Add Count"
        data-testid="pi-counts-subheader-add-count-btn"
      >
        <Plus className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Add Count</span>
      </button>
    </>
  );

  // Mobile menu buttons with titles - hide for restricted visibility
  const mobileMenuButtons = isVisibilityRestricted ? [] : [
    {
      icon: Upload,
      label: 'Upload Stock',
      onClick: () => setShowUploadStockModal(true),
      color: enconOrange
    },
    {
      icon: Plus,
      label: 'Add Count',
      onClick: () => setShowCreateCountModal(true),
      color: enconOrange
    }
  ];

  const renderCountCard = (count, variant = 'default', opts = {}) => {
    const isMonth = variant === 'month';
    const cardMinHeight = isMonth ? '220px' : '200px';
    const isCombined = count.is_combined && count.supplier_groups?.length > 1;
    const isRecount = count.is_second_count || count.recount_number > 0;
    const recountNum = count.recount_number || (count.is_second_count ? 1 : 0);
    const productCounts = count.product_counts || [];
    const supplierGroups = count.supplier_groups || [];
    const sgTotal = supplierGroups.reduce((sum, sg) => sum + (sg.total || sg.product_counts?.length || 0), 0);
    const sgCounted = supplierGroups.reduce((sum, sg) => sum + (sg.counted || 0), 0);
    const total = productCounts.length || count.products_count || sgTotal || 0;
    const counted = productCounts.length > 0
      ? productCounts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length
      : sgCounted;
    const progress = total > 0 ? Math.round((counted/total)*100) : 0;
    const isCompleted = count.status === 'completed';
    const isAtWarehouse = count.status === 'at_warehouse';
    const isScheduled = count.status === 'scheduled';

    // Chain context: when this card represents a multi-count chain (orig +
    // recounts collapsed onto one card), use a blue border and surface the
    // chain size so the user sees at a glance that there are linked counts
    // behind this card. The pills on the detail page handle navigation
    // between them.
    const chainSize = opts.chainSize || 1;
    const isChain = chainSize > 1;
    const chainBorder = '#3b82f6';   // tailwind blue-500

    // Progress label + colour (tag ABOVE location). Each extra recount
    // darkens the blue: 1=orange, 2=light blue, 3=mid blue, 4+=deep blue.
    const countStage = recountNum + 1;   // 1 for the original, 2 for 1st recount, etc.
    let progressLabel = '1st Count';
    let progressColor = enconOrange;
    if (countStage === 2) { progressLabel = '2nd Count'; progressColor = '#93c5fd'; }
    else if (countStage === 3) { progressLabel = '3rd Count'; progressColor = '#3b82f6'; }
    else if (countStage >= 4) { progressLabel = `${countStage}th Count`; progressColor = '#1e3a8a'; }

    // Location tag: Office (orange) / Warehouse (purple) / Complete (green).
    const isAtWarehouse2 = count.status === 'at_warehouse';
    const isDone2 = count.status === 'completed';
    const locationLabel = isDone2 ? 'Complete' : (isAtWarehouse2 ? 'Warehouse' : 'Office');
    const locationColor = isDone2 ? '#16a34a' : (isAtWarehouse2 ? '#8b5cf6' : enconOrange);

    // Friendly day label (e.g. "Mon 5th") shown beneath the count name.
    const friendlyDate = (() => {
      if (!count.date) return '';
      try {
        const d = new Date(count.date);
        const day = d.getDate();
        const suffix = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day % 10] || 'th');
        const dow = d.toLocaleDateString(undefined, { weekday: 'short' });
        return `${dow} ${day}${suffix}`;
      } catch (_) { return ''; }
    })();

    return (
      <div
        key={count.id}
        className="flex flex-col cursor-pointer group"
        onClick={() => navigate(`/pi-counts/${count.id}`)}
        data-testid={`count-card-${count.id}`}
      >
        <div
          className="relative bg-white rounded-2xl shadow-md group-hover:shadow-xl group-hover:scale-[1.02] transition-all duration-200 overflow-hidden flex-1"
          style={{
            minHeight: cardMinHeight,
            border: `2px solid ${enconOrange}`,
            opacity: isCompleted ? 0.75 : 1,
          }}
        >
          {isCombined && (
            <div className="absolute top-1 left-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-xs font-bold rounded">
              {count.supplier_groups.length} TABS
            </div>
          )}
          {/* Top-right SCHEDULED tag removed for schedule buckets per user
              request — the bottom location pill already says SCHEDULED, and
              the title now reads "Schedule - 5th May" so the corner tag is
              redundant. We still show it for legacy non-bucket scheduled
              docs (which have a different look). */}
          {isScheduled && !count.is_schedule_bucket && (
            <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">
              SCHEDULED
            </div>
          )}

          <div className="px-3 pt-5 pb-12 flex flex-col h-full">
            <h4 className="font-bold text-sm line-clamp-2" style={{ color: enconOrange }}>
              {count.is_schedule_bucket ? (() => {
                // Compact label: "Schedule - 5th May"
                try {
                  const d = new Date(count.date);
                  if (isNaN(d)) return count.supplier_name || 'Schedule';
                  const day = d.getDate();
                  const suf = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
                  return `Schedule - ${day}${suf} ${d.toLocaleDateString(undefined,{month:'long'})}`;
                } catch { return count.supplier_name || 'Schedule'; }
              })() : (count.supplier_name || 'Unknown')}
            </h4>
            {friendlyDate && !count.is_schedule_bucket && (
              <p className="text-xs text-gray-500 mb-2">{friendlyDate}</p>
            )}

            {/* Body: supplier-group rows OR single progress bar */}
            {isCombined && supplierGroups.length > 0 ? (
              <div className="space-y-1.5 flex-1">
                {supplierGroups.slice(0, 4).map((sg, idx) => {
                  const sgT = sg.total || 0; const sgC = sg.counted || 0;
                  const sgProgress = sgT > 0 ? Math.round((sgC / sgT) * 100) : 0;
                  return (
                    <div key={idx}>
                      <div className="flex justify-between text-xs text-gray-600 mb-0.5">
                        <span className="truncate max-w-[100px] font-medium">{sg.supplier_name || sg.name || 'Unknown'}</span>
                        <span className="text-gray-500">{sgC}/{sgT}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all" style={{ width: `${sgProgress}%`, backgroundColor: sgProgress === 100 ? '#22c55e' : enconOrange }} />
                      </div>
                    </div>
                  );
                })}
                {supplierGroups.length > 4 && <p className="text-xs text-gray-400">+{supplierGroups.length - 4} more groups</p>}
              </div>
            ) : total > 0 ? (
              <div className="mb-2">
                <div className="flex justify-between text-sm text-gray-600 mb-1">
                  <span className="font-semibold">{counted}/{total}</span>
                  <span>{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: counted === total ? '#22c55e' : enconOrange }} />
                </div>
                {/* Schedule buckets — show first-3-char prefixes of every
                    item in the bucket so the user can see at a glance which
                    supplier ranges are about to be counted. Deduped + sorted. */}
                {count.is_schedule_bucket && productCounts.length > 0 && (() => {
                  const prefixes = Array.from(new Set(
                    productCounts.map(p => ((p.product_code || p.code || '').toUpperCase().slice(0, 3))).filter(Boolean)
                  )).sort();
                  if (prefixes.length === 0) return null;
                  return (
                    <p className="mt-2 text-[10px] text-gray-600 leading-tight" data-testid={`bucket-prefixes-${count.id}`}>
                      {prefixes.join(', ')}
                    </p>
                  );
                })()}
              </div>
            ) : null}

            {typeof count.discrepancy_count === 'number' && count.discrepancy_count > 0 && (
              <p className="text-xs text-red-600 font-semibold">{count.discrepancy_count} discrepancies</p>
            )}
          </div>

          {/* Tags pinned to the very bottom of the card so they sit on the
              same baseline regardless of card content height. */}
          <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center gap-1 px-2">
            <span
              className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white"
              style={{ backgroundColor: progressColor }}
              data-testid={`card-progress-tag-${count.id}`}
            >
              {progressLabel}
            </span>
            <span
              className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white"
              style={{ backgroundColor: locationColor }}
              data-testid={`card-location-tag-${count.id}`}
            >
              {locationLabel}
            </span>
          </div>
        </div>
      </div>
    );
  };

  // Helper: render a list of cards. Cards that share an `original_count_id`
  // collapse into a SINGLE card that opens the most recent count by default.
  // The user can hop to other counts in the chain via the linked-count pills
  // on the detail page itself, so we no longer need to render every recount
  // as its own card. Grouped (chain) cards use a blue border to visually
  // differentiate them from solo counts.
  const renderGroupedCards = (counts, variant = 'default') => {
    if (!counts || counts.length === 0) return null;
    const groups = new Map();
    counts.forEach(c => {
      const rootId = c.original_count_id || c.id;
      if (!groups.has(rootId)) groups.set(rootId, []);
      groups.get(rootId).push(c);
    });
    const out = [];
    groups.forEach((group, rootId) => {
      // Sort within group: highest recount_number first → that's the "latest"
      // count, which is what the chain card should open by default.
      group.sort((a, b) => (b.recount_number || 0) - (a.recount_number || 0));
      const latest = group[0];
      const chainSize = group.length;
      out.push(renderCountCard(latest, variant, { chainSize, rootId }));
    });
    return out;
  };

  return (
    <div className={`v3-pg v3-pi min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 ${isDragging ? 'ring-4 ring-inset ring-orange-400' : ''}`}>
      {/* Drag overlay for CSV upload */}
      {isDragging && (
        <div className="fixed inset-0 bg-orange-100 bg-opacity-90 z-[100] flex items-center justify-center">
          <div className="text-center">
            <Upload className="w-20 h-20 mx-auto mb-4" style={{ color: enconOrange }} />
            <p className="text-2xl font-bold" style={{ color: enconOrange }}>
              Drop Stock Report Here
            </p>
            <p className="text-gray-600 mt-2">Release to upload CSV file</p>
          </div>
        </div>
      )}
      
      {/* Global Header with page-specific buttons */}
      <AppHeader user={user} subHeaderLeft={subHeaderLeft} subHeaderRight={subHeaderRight} mobileMenuButtons={mobileMenuButtons} />
      
      {/* Main Content — minimal padding since notification bar is in document flow */}
      <div className="px-4 lg:px-8 pt-[20px] pb-6">
        <div className="max-w-[1400px] mx-auto v3-page-content">
        
        {/* Quick Status Buttons Row */}
        {!isVisibilityRestricted && (
          <div className="mb-3">
            {/* P.I Counts Title */}
            <h2 className="text-lg font-bold text-gray-800 mb-3">P.I Counts</h2>
            
            <div className="flex flex-wrap justify-center gap-3">
              {/* Action Required tab — orange, in front of Warehouse. Opens
                  automatically when it has items. */}
              <div className="relative">
                <button
                  onClick={() => { setShowActionSection(!showActionSection); setShowSentToWarehouseSection(false); setShowDiscrepanciesSection(false); setShowAdjustmentsSection(false); setShowScheduleSection(false); }}
                  className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all relative z-10"
                  style={{ backgroundColor: '#fff7ed', color: '#ee7203', border: '2px solid #ee7203' }}
                  data-testid="pill-action-required"
                >
                  <AlertCircle className="w-4 h-4" />
                  Action Required ({(returnedFromWarehouse?.length || 0) + (incompleteCounts?.length || 0)})
                  {showActionSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showActionSection && (
                  <div className="absolute left-4 right-4 top-full h-3 z-0" style={{ backgroundColor: '#fff7ed', borderLeft: '2px solid #fed7aa', borderRight: '2px solid #fed7aa' }} />
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => { setShowSentToWarehouseSection(!showSentToWarehouseSection); setShowActionSection(false); setShowDiscrepanciesSection(false); setShowAdjustmentsSection(false); setShowScheduleSection(false); }}
                  className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all relative z-10"
                  style={{ backgroundColor: '#eef2ff', color: '#6366f1', border: '2px solid #6366f1' }}
                  data-testid="pill-warehouse"
                >
                  <Warehouse className="w-4 h-4" />
                  Warehouse ({pendingAtWarehouse.filter(c => c.type !== 'discrepancy_recount').length + discrepancyReportsAtWarehouse.length})
                  {showSentToWarehouseSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showSentToWarehouseSection && (
                  <div className="absolute left-4 right-4 top-full h-3 z-0" style={{ backgroundColor: '#eef2ff', borderLeft: '2px solid #a5b4fc', borderRight: '2px solid #a5b4fc' }} />
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => { setShowDiscrepanciesSection(!showDiscrepanciesSection); setShowActionSection(false); setShowAdjustmentsSection(false); setShowScheduleSection(false); setShowSentToWarehouseSection(false); }}
                  className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all relative z-10"
                  style={{ backgroundColor: '#fef2f2', color: '#ef4444', border: '2px solid #ef4444' }}
                  data-testid="pill-scheduled"
                >
                  <Calendar className="w-4 h-4" />
                  Scheduled ({(() => { const byDay = new Map(); (scheduledCounts||[]).forEach(c => { if (!byDay.has(c.date)) byDay.set(c.date, 0); byDay.set(c.date, byDay.get(c.date)+1); }); return byDay.size; })()})
                  {showDiscrepanciesSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showDiscrepanciesSection && (
                  <div className="absolute left-4 right-4 top-full h-3 z-0" style={{ backgroundColor: '#fef2f2', borderLeft: '2px solid #fecaca', borderRight: '2px solid #fecaca' }} />
                )}
              </div>
              <div className="relative">
                <button
                  onClick={() => { setShowAdjustmentsSection(!showAdjustmentsSection); setShowActionSection(false); setShowDiscrepanciesSection(false); setShowScheduleSection(false); setShowSentToWarehouseSection(false); }}
                  className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all relative z-10"
                  style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #10b981 100%)', color: 'white', border: '2px solid #10b981' }}
                  data-testid="pill-adjustments"
                >
                  <ClipboardList className="w-4 h-4" />
                  Adjustments ({pendingAdjustments.length})
                  {showAdjustmentsSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showAdjustmentsSection && (
                  <div className="absolute left-4 right-4 top-full h-3 z-0" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #d1fae5 100%)', borderLeft: '2px solid #5eead4', borderRight: '2px solid #5eead4' }} />
                )}
              </div>
              <div className="relative ml-auto flex items-center gap-2">
                {/* Report button (super_admin always; others need can_access_pi_reports).
                    Drop-down menu — Create New + Saved Reports. */}
                {(user?.role === 'super_admin' || user?.can_access_pi_reports) && (
                  <div className="relative">
                    <button
                      onClick={() => setShowReportMenu(v => !v)}
                      className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all text-white"
                      style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
                      data-testid="report-menu-btn"
                      title="Reports"
                    >
                      <Sparkles className="w-4 h-4" />
                      Report
                      <ChevronDown className={`w-4 h-4 transition-transform ${showReportMenu ? 'rotate-180' : ''}`} />
                    </button>
                    {showReportMenu && (
                      <>
                        <div className="fixed inset-0 z-20" onClick={() => setShowReportMenu(false)} />
                        <div
                          className="absolute z-30 right-0 mt-1 bg-white rounded-xl border-2 shadow-xl min-w-[180px] overflow-hidden"
                          style={{ borderColor: '#7c3aed' }}
                          data-testid="report-menu-dropdown"
                        >
                          <button
                            className="w-full px-4 py-3 text-left hover:bg-violet-50 flex items-center gap-3 text-sm font-medium"
                            onClick={() => { setShowReportMenu(false); setShowCreateReportModal(true); }}
                            data-testid="report-menu-create"
                          >
                            <Sparkles className="w-4 h-4 text-violet-600" />
                            Create New
                          </button>
                          <button
                            className="w-full px-4 py-3 text-left hover:bg-violet-50 flex items-center gap-3 text-sm font-medium border-t"
                            onClick={() => { setShowReportMenu(false); setShowSavedReportsModal(true); }}
                            data-testid="report-menu-saved"
                          >
                            <FileText className="w-4 h-4 text-violet-600" />
                            Saved Reports
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <button
                  onClick={() => { setShowScheduleSection(!showScheduleSection); setShowActionSection(false); setShowDiscrepanciesSection(false); setShowAdjustmentsSection(false); setShowSentToWarehouseSection(false); }}
                  className="px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-all relative z-10"
                  style={{ backgroundColor: 'white', color: '#7c3aed', border: '2px solid #7c3aed' }}
                  data-testid="pill-schedule"
                >
                  <Calendar className="w-4 h-4" />
                  Schedule
                  {showScheduleSection ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                {showScheduleSection && (
                  <div className="absolute left-4 right-4 top-full h-3 z-0" style={{ backgroundColor: '#f5f3ff', borderLeft: '2px solid #c4b5fd', borderRight: '2px solid #c4b5fd' }} />
                )}
              </div>
            </div>
        
            {/* Scheduled Counts Section Dropdown — shows one card per scheduled day
                (containing every count scheduled for that day) until they are
                sent to W/H, completed, or adjusted. */}
            {showDiscrepanciesSection && (
              <div className="mt-3 mb-4 bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold flex items-center gap-2 text-red-700">
                    <Calendar className="w-5 h-5" />
                    Scheduled
                  </h2>
                  <p className="text-sm text-red-600">One card per scheduled day. Click to see the counts for that day.</p>
                </div>
                {(!scheduledCounts || scheduledCounts.length === 0) ? (
                  <div className="text-center py-6 text-gray-500">
                    <Calendar className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>Nothing scheduled</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {(() => {
                      // Group scheduled counts by date.
                      const byDay = new Map();
                      (scheduledCounts || []).forEach(c => {
                        const d = c.date || '';
                        if (!byDay.has(d)) byDay.set(d, []);
                        byDay.get(d).push(c);
                      });
                      const entries = Array.from(byDay.entries()).sort((a,b) => a[0].localeCompare(b[0]));
                      return entries.map(([date, items]) => {
                        const totalItems = items.reduce((sum, c) => sum + ((c.product_counts || []).length || c.products_count || 0), 0);
                        // Friendly day label "Monday 5th May"
                        const friendly = (() => {
                          try {
                            const dd = new Date(date);
                            const day = dd.getDate();
                            const suf = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
                            return `${dd.toLocaleDateString(undefined,{weekday:'long'})} ${day}${suf} ${dd.toLocaleDateString(undefined,{month:'long'})}`;
                          } catch (_) { return date; }
                        })();
                        return (
                          <div
                            key={date}
                            className="flex flex-col cursor-pointer group"
                            onClick={() => { setSelectedDate(date); setShowScheduleSection(true); setShowDiscrepanciesSection(false); }}
                            data-testid={`scheduled-day-${date}`}
                          >
                            <div
                              className="relative bg-white rounded-2xl border-2 p-3 shadow-md group-hover:shadow-xl group-hover:scale-[1.02] transition-all duration-200 flex flex-col"
                              style={{ minHeight: '200px', aspectRatio: '1 / 1', borderColor: '#ef4444' }}
                            >
                              <p className="text-sm font-bold text-red-700 truncate">{items[0]?.supplier_name || `${items.length} counts`}</p>
                              <p className="text-xs text-gray-500 mt-0.5">{friendly}</p>
                              <div className="mt-3 text-center">
                                <p className="text-3xl font-bold text-red-800">{items.length}</p>
                                <p className="text-[11px] text-gray-500">count{items.length === 1 ? '' : 's'} scheduled · {totalItems} item{totalItems === 1 ? '' : 's'}</p>
                              </div>
                              <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#ef4444' }}>Scheduled</span>
                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: enconOrange }}>Office</span>
                              </div>
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}
              </div>
            )}

            {/* Sent to Warehouse Section Dropdown */}
            {showSentToWarehouseSection && (
              <div className="mt-3 mb-4 bg-indigo-50 border-2 border-indigo-200 rounded-xl p-4">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-indigo-700 mb-3">
                  <Warehouse className="w-5 h-5" />
                  Sent to Warehouse
                </h2>
                {pendingAtWarehouse.length === 0 && discrepancyReportsAtWarehouse.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <Warehouse className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No counts at warehouse</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {pendingAtWarehouse.filter(c => c.type !== 'discrepancy_recount').map((count) => {
                      const productCounts = count.product_counts || [];
                      const total = productCounts.length || count.products_count || 0;
                      const counted = productCounts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length;
                      const isRecount = count.is_second_count || count.recount_number > 0;
                      const recountNum = count.recount_number || (count.is_second_count ? 1 : 0);
                      const progress = total > 0 ? Math.round((counted / total) * 100) : 0;
                      const supplierGroups = count.supplier_groups || [];
                      const isCombined = count.is_combined && supplierGroups.length > 1;
                      // Progress tag (matches week/month card)
                      const countStage = recountNum + 1;
                      let progressLabel = '1st Count'; let progressColor = enconOrange;
                      if (countStage === 2) { progressLabel = '2nd Count'; progressColor = '#93c5fd'; }
                      else if (countStage === 3) { progressLabel = '3rd Count'; progressColor = '#3b82f6'; }
                      else if (countStage >= 4) { progressLabel = `${countStage}th Count`; progressColor = '#1e3a8a'; }

                      return (
                        <div
                          key={count.id}
                          className="flex flex-col cursor-pointer group"
                          onClick={() => navigate(`/pi-counts/${count.id}`)}
                          data-testid={`wh-card-${count.id}`}
                        >
                          <div
                            className="relative bg-white rounded-2xl border-2 p-3 shadow-md group-hover:shadow-xl group-hover:scale-[1.02] transition-all duration-200 flex flex-col"
                            style={{ minHeight: '200px', aspectRatio: '1 / 1', borderColor: '#8b5cf6' }}
                          >
                            <p className="text-sm font-bold text-indigo-700 truncate" title={count.supplier_name}>
                              {count.supplier_name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{safeFormatDate(count.date, 'EEE do')}</p>
                            {count.assigned_to && (
                              <div className="mt-1.5 px-2 py-0.5 rounded bg-amber-50 border border-amber-300 inline-flex items-center gap-1 self-start">
                                <User className="w-3 h-3 text-amber-600" />
                                <span className="text-[10px] font-bold text-amber-700">{count.assigned_to}</span>
                              </div>
                            )}
                            {isCombined ? (
                              <div className="space-y-1 mt-2 flex-1">
                                {supplierGroups.slice(0, 3).map((sg, idx) => {
                                  const sgT = sg.total || 0; const sgC = sg.counted || 0;
                                  const sgP = sgT > 0 ? Math.round((sgC / sgT) * 100) : 0;
                                  return (
                                    <div key={idx}>
                                      <div className="flex justify-between text-[10px] text-gray-600"><span className="truncate max-w-[80px]">{sg.supplier_name || sg.name}</span><span>{sgC}/{sgT}</span></div>
                                      <div className="w-full bg-gray-200 rounded-full h-1.5 mt-0.5"><div className="h-1.5 rounded-full" style={{ width: `${sgP}%`, backgroundColor: sgP === 100 ? '#22c55e' : '#8b5cf6' }} /></div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs font-semibold"><span>{counted}/{total}</span><span className="text-gray-500">{progress}%</span></div>
                                <div className="w-full bg-gray-200 rounded-full h-2 mt-1"><div className="h-2 rounded-full" style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#22c55e' : '#8b5cf6' }} /></div>
                              </div>
                            )}
                            {/* Centre-bottom tag stack */}
                            <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: progressColor }}>{progressLabel}</span>
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#8b5cf6' }}>Warehouse</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* Discrepancy Recounts at warehouse — same unified style */}
                    {discrepancyReportsAtWarehouse.map((report) => {
                      const items = report.items || [];
                      const total = items.length || report.items_count || 0;
                      const counted = items.filter(i => i.recount_value !== null && i.recount_value !== undefined).length;
                      const progress = total > 0 ? Math.round((counted / total) * 100) : 0;
                      return (
                        <div
                          key={report.id}
                          className="flex flex-col cursor-pointer group"
                          onClick={() => navigate(`/pi-counts/discrepancy/${report.id}`)}
                          data-testid={`wh-disc-card-${report.id}`}
                        >
                          <div
                            className="relative bg-white rounded-2xl border-2 p-3 shadow-md group-hover:shadow-xl group-hover:scale-[1.02] transition-all duration-200 flex flex-col"
                            style={{ minHeight: '200px', aspectRatio: '1 / 1', borderColor: '#8b5cf6' }}
                          >
                            <p className="text-sm font-bold text-indigo-700 truncate">{report.supplier_name || 'Discrepancy'}</p>
                            <p className="text-xs text-gray-500 mt-0.5">WC {safeFormatDate(report.week_commencing, 'dd MMM')}</p>
                            <div className="mt-2">
                              <div className="flex justify-between text-xs font-semibold"><span>{counted}/{total}</span><span className="text-gray-500">{progress}%</span></div>
                              <div className="w-full bg-gray-200 rounded-full h-2 mt-1"><div className="h-2 rounded-full" style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#22c55e' : '#8b5cf6' }} /></div>
                            </div>
                            <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#ef4444' }}>Discrepancy</span>
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#8b5cf6' }}>Warehouse</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Adjustments Section Dropdown */}
            {showAdjustmentsSection && (
              <div className="mt-3 mb-4 bg-gradient-to-r from-blue-50 to-green-50 border-2 border-teal-200 rounded-xl p-4">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-teal-700 mb-3">
                  <ClipboardList className="w-5 h-5" />
                  Pending Adjustments
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
                  <div>
                {pendingAdjustments.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <ClipboardList className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    <p>No pending adjustments</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {pendingAdjustments.map((adjustment) => {
                      const itemCount = adjustment.items?.length || 0;
                      const totalValue = adjustment.items?.reduce((sum, item) => {
                        const qty = item.adjustment_quantity || item.quantity || 0;
                        const cost = item.cost || item.unit_cost || 0;
                        return sum + (qty * cost);
                      }, 0) || 0;
                      // Friendly date "Monday 5th May"
                      const refDate = adjustment.count_date || adjustment.created_at;
                      const friendly = (() => {
                        try {
                          const dd = new Date(refDate);
                          const day = dd.getDate();
                          const suf = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
                          return `${dd.toLocaleDateString(undefined,{weekday:'long'})} ${day}${suf} ${dd.toLocaleDateString(undefined,{month:'long'})}`;
                        } catch (_) { return ''; }
                      })();
                      return (
                        <div
                          key={adjustment.id}
                          className="flex flex-col cursor-pointer group"
                          onClick={() => navigate(`/pi-counts/adjustment/${adjustment.id}`)}
                          data-testid={`adj-card-${adjustment.id}`}
                        >
                          <div
                            className="relative bg-white rounded-2xl border-2 p-3 shadow-md group-hover:shadow-xl group-hover:scale-[1.02] transition-all duration-200 flex flex-col"
                            style={{ minHeight: '200px', aspectRatio: '1 / 1', borderColor: '#10b981' }}
                          >
                            <p className="text-sm font-bold text-teal-700 truncate" title={adjustment.count_name}>
                              {adjustment.count_name || 'Adjustment'}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">{friendly}</p>
                            <div className="mt-3 text-center">
                              <p className="text-3xl font-bold text-teal-800">{itemCount}</p>
                              <p className="text-[11px] text-gray-500">item{itemCount === 1 ? '' : 's'} to adjust</p>
                            </div>
                            {totalValue > 0 && (
                              <p className="text-xs text-teal-600 mt-1 text-center">£{totalValue.toFixed(2)}</p>
                            )}
                            <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#10b981' }}>
                                Adjustment
                              </span>
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: enconOrange }}>
                                Office
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                  </div>

                  {/* Completed Adjustments running-total card — compact view
                      shows just the month count. Click to open a modal with
                      the full grouped list. */}
                  <div className="bg-white border-2 rounded-2xl p-4 self-start cursor-pointer hover:shadow-md transition-all" style={{ borderColor: '#10b981' }} onClick={() => setShowCompletedAdjustmentsModal(true)} data-testid="completed-adjustments-card">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                        <CheckCircle className="w-4 h-4" />
                        Completed
                      </h3>
                    </div>
                    {(() => {
                      // Filter to the currently-selected month/year so the
                      // count matches what the user is viewing.
                      const monthFiltered = (completedAdjustments || []).filter(a => {
                        const ts = a.completed_at || a.updated_at || a.created_at || '';
                        if (!ts) return false;
                        const d = new Date(ts);
                        return !isNaN(d) && d.getFullYear() === currentYear && d.getMonth() === selectedMonth;
                      });
                      const monthLabel = (() => {
                        try { return new Date(currentYear, selectedMonth, 1).toLocaleString(undefined, { month: 'long' }); }
                        catch { return ''; }
                      })();
                      return (
                        <div className="text-center py-2" data-testid="completed-adj-month-count">
                          <p className="text-4xl font-bold text-emerald-700">{monthFiltered.length}</p>
                          <p className="text-[11px] text-gray-500 mt-1">in {monthLabel}</p>
                          <p className="text-[10px] text-emerald-600 mt-1 underline-offset-2 hover:underline">Click for details</p>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Section Dropdown */}
            {showScheduleSection && (
              <div className="mt-3 mb-4" style={{ marginTop: '12px' }}>
                <PICountSchedule isOpen={true} onClose={() => setShowScheduleSection(false)} />
              </div>
            )}
          </div>
        )}
        
        {/* ACTION COUNTS TABLE - Fixed Size */}
        {!isVisibilityRestricted && showActionSection && (
          <div className="mb-6 bg-white rounded-xl border border-gray-900 p-4" style={{ minHeight: '220px' }}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ color: enconOrange }}>
                <AlertCircle className="w-6 h-6" />
                Action Required
              </h2>
              {/* Inline status updates — each clickable, scrolls to & opens
                  the matching section, coloured to match its pill. */}
              {(() => {
                const wh = pendingAtWarehouse.filter(c => c.type !== 'discrepancy_recount').length + discrepancyReportsAtWarehouse.length;
                const sched = (() => { const s = new Set(); (scheduledCounts||[]).forEach(c => s.add(c.date)); return s.size; })();
                const adj = pendingAdjustments.length;
                if (wh + sched + adj === 0) return null;
                const Pill = ({label, color, n, onClick, testid}) => (
                  <button onClick={onClick} className="text-xs font-semibold hover:underline transition-all" style={{ color }} data-testid={testid}>
                    {n} {label}
                  </button>
                );
                const focus = (setter) => () => {
                  setShowActionSection(false);
                  setShowSentToWarehouseSection(false);
                  setShowDiscrepanciesSection(false);
                  setShowAdjustmentsSection(false);
                  setShowScheduleSection(false);
                  setter(true);
                  setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
                };
                return (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1" data-testid="inline-status-action">
                    {wh > 0 && <Pill label={`in warehouse`} color="#6366f1" n={wh} onClick={focus(setShowSentToWarehouseSection)} testid="inline-status-warehouse" />}
                    {sched > 0 && <Pill label={`scheduled`} color="#ef4444" n={sched} onClick={focus(setShowDiscrepanciesSection)} testid="inline-status-scheduled" />}
                    {adj > 0 && <Pill label={`adjustment${adj === 1 ? '' : 's'}`} color="#0d9488" n={adj} onClick={focus(setShowAdjustmentsSection)} testid="inline-status-adjustment" />}
                  </div>
                );
              })()}
            </div>
            {(() => {
              const actionItems = [
                ...returnedFromWarehouse.map(c => ({ ...c, _kind: 'count', actionType: 'Office Check', actionColor: '#ee7203' })),
                ...incompleteCounts.map(c => ({ ...c, _kind: 'count', actionType: 'Ready to Count', actionColor: '#ee7203' })),
                ...pendingAdjustments.map(a => ({ ...a, _kind: 'adjustment', actionType: 'Pending Adjustment', actionColor: '#0d9488' }))
              ];
              
              const atWarehouseCount = pendingAtWarehouse.filter(c => c.type !== 'discrepancy_recount').length + discrepancyReportsAtWarehouse.length;
              const discrepancyCount = discrepancyReports.length;
              const adjustmentCount = pendingAdjustments.length;
              
              if (actionItems.length === 0) {
                return (
                  <div className="flex flex-col items-center justify-center py-6">
                    <CheckCircle className="w-12 h-12 mb-3 text-green-500" />
                    <p className="text-gray-500 font-medium text-base mb-3">No immediate actions required</p>
                    <div className="flex flex-wrap justify-center gap-4 text-base">
                      {atWarehouseCount > 0 && (
                        <button 
                          onClick={() => { setShowSentToWarehouseSection(!showSentToWarehouseSection); setShowDiscrepanciesSection(false); setShowAdjustmentsSection(false); setShowScheduleSection(false); }}
                          className="flex items-center gap-1.5 text-indigo-600 hover:text-indigo-800 hover:underline font-medium transition-colors"
                        >
                          <Warehouse className="w-5 h-5" />
                          {atWarehouseCount} with Warehouse
                        </button>
                      )}
                      {discrepancyCount > 0 && (
                        <button 
                          onClick={() => { setShowDiscrepanciesSection(!showDiscrepanciesSection); setShowSentToWarehouseSection(false); setShowAdjustmentsSection(false); setShowScheduleSection(false); }}
                          className="flex items-center gap-1.5 text-red-600 hover:text-red-800 hover:underline font-medium transition-colors"
                        >
                          <AlertTriangle className="w-5 h-5" />
                          {discrepancyCount} in Discrepancies
                        </button>
                      )}
                      {adjustmentCount > 0 && (
                        <button 
                          onClick={() => { setShowAdjustmentsSection(!showAdjustmentsSection); setShowDiscrepanciesSection(false); setShowSentToWarehouseSection(false); setShowScheduleSection(false); }}
                          className="flex items-center gap-1.5 text-teal-600 hover:text-teal-800 hover:underline font-medium transition-colors"
                        >
                          <ClipboardList className="w-5 h-5" />
                          {adjustmentCount} due for Adjustment
                        </button>
                      )}
                    </div>
                  </div>
                );
              }
              
              return (
                <div className="flex flex-wrap gap-4 justify-center">
                  {actionItems.map((count) => {
                    // ── ADJUSTMENT CARD ──────────────────────────────────────
                    if (count._kind === 'adjustment') {
                      const itemCount = count.items?.length || 0;
                      const totalValue = count.items?.reduce((sum, item) => {
                        const qty = item.adjustment_quantity || item.quantity || 0;
                        const cost = item.cost || item.unit_cost || 0;
                        return sum + (qty * cost);
                      }, 0) || 0;
                      const refDate = count.count_date || count.created_at;
                      const friendly = (() => {
                        try {
                          const dd = new Date(refDate);
                          const day = dd.getDate();
                          const suf = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
                          return `${dd.toLocaleDateString(undefined,{weekday:'short'})} ${day}${suf}`;
                        } catch (_) { return ''; }
                      })();
                      return (
                        <div
                          key={count.id}
                          className="relative bg-white rounded-2xl border-2 shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-200 cursor-pointer overflow-hidden"
                          style={{ borderColor: '#0d9488', minHeight: '240px', flex: '0 0 210px', width: '210px' }}
                          onClick={() => navigate(`/pi-counts/adjustment/${count.id}`)}
                          data-testid={`action-adj-card-${count.id}`}
                        >
                          <div className="absolute top-0 left-0 right-0 px-3 py-1.5 text-white text-sm font-bold flex items-center gap-1.5" style={{ background: '#0d9488' }}>
                            <ClipboardList className="w-4 h-4" />
                            Pending Adjustment
                          </div>
                          <div className="pt-8 p-3 flex flex-col h-full">
                            <h3 className="font-bold text-base mb-1 line-clamp-2" style={{ color: '#0d9488' }}>
                              {count.count_name || 'Adjustment'}
                            </h3>
                            <p className="text-sm text-gray-500">{friendly}</p>
                            <div className="mt-3 text-center">
                              <p className="text-3xl font-bold" style={{ color: '#0d9488' }}>{itemCount}</p>
                              <p className="text-[11px] text-gray-500">item{itemCount === 1 ? '' : 's'} to adjust</p>
                            </div>
                            {totalValue > 0 && (
                              <p className="text-xs mt-1 text-center" style={{ color: '#0d9488' }}>£{totalValue.toFixed(2)}</p>
                            )}
                            <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: '#0d9488' }}>
                                Adjustment
                              </span>
                              <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: enconOrange }}>
                                Office
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // ── COUNT CARD ────────────────────────────────────────────
                    const productCounts = count.product_counts || [];
                    const total = productCounts.length || count.products_count || 0;
                    const counted = productCounts.filter(p => p.physical_count !== null && p.physical_count !== undefined).length;
                    const isRecount = count.is_second_count || count.recount_number > 0;
                    const recountNum = count.recount_number || (count.is_second_count ? 1 : 0);
                    const progress = total > 0 ? Math.round((counted/total)*100) : 0;
                    const supplierGroups = count.supplier_groups || [];
                    const isCombined = count.is_combined && supplierGroups.length > 1;
                    
                    const isOfficeCheck = count.actionType === 'Office Check';
                    const cardBorderColor = '#ee7203';
                    const headerBg = count.actionColor;
                    const isMenuOpen = quickMenuCountId === count.id;
                    // Progress/location tag colours (unified with the week/month card)
                    const countStage = recountNum + 1;
                    let progressLabel = '1st Count'; let progressColor = enconOrange;
                    if (countStage === 2) { progressLabel = '2nd Count'; progressColor = '#93c5fd'; }
                    else if (countStage === 3) { progressLabel = '3rd Count'; progressColor = '#3b82f6'; }
                    else if (countStage >= 4) { progressLabel = `${countStage}th Count`; progressColor = '#1e3a8a'; }
                    const locAtWh = count.status === 'at_warehouse';
                    const locDone = count.status === 'completed';
                    const locationLabel = locDone ? 'Complete' : (locAtWh ? 'Warehouse' : 'Office');
                    const locationColor = locDone ? '#16a34a' : (locAtWh ? '#8b5cf6' : enconOrange);
                    
                    return (
                      <div
                        key={count.id}
                        className="relative bg-white rounded-2xl border-2 shadow-md hover:shadow-xl hover:scale-[1.02] transition-all duration-200 cursor-pointer overflow-hidden group"
                        style={{ borderColor: cardBorderColor, minHeight: '240px', flex: '0 0 210px', width: '210px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setQuickMenuCountId(isMenuOpen ? null : count.id);
                        }}
                      >
                        <div className="absolute top-0 left-0 right-0 px-3 py-1.5 text-white text-sm font-bold flex items-center gap-1.5" style={{ background: headerBg }}>
                          <AlertCircle className="w-4 h-4" />
                          {count.actionType}
                        </div>
                        <div className="pt-8 p-3 flex flex-col h-full">
                          <h3 className="font-bold text-base mb-1 line-clamp-2" style={{ color: enconOrange }}>
                            {count.supplier_name || 'Unknown'}
                          </h3>
                          <p className="text-sm text-gray-500">{safeFormatDate(count.date, 'EEE do')}</p>
                          {count.counted_by_name && (
                            <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full bg-green-50 border border-green-200">
                              <svg className="w-3 h-3 text-green-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                              <span className="text-[11px] font-semibold text-green-700 truncate max-w-[130px]">{count.counted_by_name}</span>
                            </div>
                          )}
                          
                          {isCombined ? (
                            <div className="space-y-1.5 mt-2 flex-1">
                              {supplierGroups.slice(0, 3).map((sg, idx) => {
                                const sgTotal = sg.total || 0;
                                const sgCounted = sg.counted || 0;
                                const sgProgress = sgTotal > 0 ? Math.round((sgCounted / sgTotal) * 100) : 0;
                                return (
                                  <div key={idx}>
                                    <div className="flex justify-between text-xs text-gray-600">
                                      <span className="truncate max-w-[80px] font-medium">{sg.supplier_name || sg.name}</span>
                                      <span>{sgCounted}/{sgTotal}</span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-2 mt-0.5">
                                      <div className="h-2 rounded-full" style={{ width: `${sgProgress}%`, backgroundColor: sgProgress === 100 ? '#22c55e' : enconOrange }} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : total > 0 ? (
                            <div className="mt-2">
                              <div className="flex justify-between text-sm text-gray-600 mb-1">
                                <span className="font-semibold">{counted}/{total}</span>
                                <span>{progress}%</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="h-2 rounded-full" style={{ width: `${progress}%`, backgroundColor: counted === total ? '#22c55e' : enconOrange }} />
                              </div>
                            </div>
                          ) : null}

                          {/* Centre-bottom tag stack: progress + location */}
                          <div className="mt-auto pt-2 flex flex-col items-center gap-1">
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: progressColor }} data-testid={`today-progress-tag-${count.id}`}>
                              {progressLabel}
                            </span>
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full text-white" style={{ backgroundColor: locationColor }} data-testid={`today-location-tag-${count.id}`}>
                              {locationLabel}
                            </span>
                          </div>
                        </div>

                        {/* Quick Action Menu */}
                        {isMenuOpen && (
                          <div
                            className="absolute bottom-4 right-4 bg-white rounded-xl border-2 shadow-xl z-20 min-w-[160px]"
                            style={{ borderColor: enconOrange }}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`quick-menu-${count.id}`}
                          >
                            <button
                              className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium"
                              onClick={() => { setQuickMenuCountId(null); navigate(`/pi-counts/${count.id}`); }}
                              data-testid={`quick-menu-open-${count.id}`}
                            >
                              <FileCheck className="w-5 h-5" style={{ color: enconOrange }} />
                              Open Count
                            </button>
                            <button
                              className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium border-t"
                              onClick={() => handleQuickMenuSendToWarehouse(count.id)}
                              data-testid={`quick-menu-send-wh-${count.id}`}
                            >
                              <Send className="w-5 h-5 text-indigo-500" />
                              Send to W/H
                            </button>
                            <button
                              className="w-full px-4 py-3 text-left hover:bg-orange-50 flex items-center gap-3 text-sm font-medium border-t"
                              onClick={() => handleQuickMenuPrint(count.id)}
                              data-testid={`quick-menu-print-${count.id}`}
                            >
                              <Printer className="w-5 h-5 text-gray-600" />
                              Print
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
        
        {/* Returned from Warehouse - MOVED TO ACTIONS SECTION ABOVE */}
        
        {/* Counts Grouped by Week - Visible to ALL users (including restricted) */}
        <div>
          {/* Month Selector Bar - Full Width */}
          <div className="bg-white rounded-xl border border-gray-900 p-3 w-full mb-4">
            <div className="flex items-center gap-2 w-full">
              {/* Previous Year Arrow */}
              <button
                onClick={() => setCurrentYear(currentYear - 1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-all flex-shrink-0"
                style={{ color: enconOrange }}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              
              {/* Year Display */}
              <span className="text-base font-bold text-gray-700 min-w-[50px] text-center">{currentYear}</span>
              
              {/* Months Grid - Full Width */}
              <div className="flex-1 grid grid-cols-12 gap-1">
                {months.map((month, index) => (
                  <button
                    key={month}
                    onClick={() => setSelectedMonth(index)}
                    className={`py-2 rounded-lg font-semibold text-sm transition-all ${
                      selectedMonth === index
                        ? 'text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    style={
                      selectedMonth === index
                        ? { backgroundColor: enconOrange }
                        : {}
                    }
                  >
                    {month.substring(0, 3)}
                  </button>
                ))}
              </div>
              
              {/* Next Year Arrow */}
              <button
                onClick={() => setCurrentYear(currentYear + 1)}
                className="p-1.5 rounded-lg hover:bg-gray-100 transition-all flex-shrink-0"
                style={{ color: enconOrange }}
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-900 p-4 md:p-6">
          {/* Title + inline status pills on the same row, left-aligned */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-3" data-testid="inline-status-month">
            <h2 className="text-xl font-bold" style={{ color: enconBlack }}>
              {months[selectedMonth]} {currentYear} {isVisibilityRestricted ? 'Completed ' : ''}Counts
            </h2>
            {(() => {
              const act = (returnedFromWarehouse?.length || 0) + (incompleteCounts?.length || 0);
              const wh = pendingAtWarehouse.filter(c => c.type !== 'discrepancy_recount').length + discrepancyReportsAtWarehouse.length;
              const sched = (() => { const s = new Set(); (scheduledCounts||[]).forEach(c => s.add(c.date)); return s.size; })();
              const adj = pendingAdjustments.length;
              if (act + wh + sched + adj === 0) return null;
              const Pill = ({label, color, n, onClick, testid}) => (
                <button onClick={onClick} className="text-xs font-semibold hover:underline transition-all" style={{ color }} data-testid={testid}>
                  {n} {label}
                </button>
              );
              const focus = (setter) => () => {
                setShowActionSection(false);
                setShowSentToWarehouseSection(false);
                setShowDiscrepanciesSection(false);
                setShowAdjustmentsSection(false);
                setShowScheduleSection(false);
                setter(true);
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50);
              };
              return (
                <>
                  {act > 0 && <Pill label="action required" color="#ee7203" n={act} onClick={focus(setShowActionSection)} testid="inline-status-month-action" />}
                  {wh > 0 && <Pill label="in warehouse" color="#6366f1" n={wh} onClick={focus(setShowSentToWarehouseSection)} testid="inline-status-month-warehouse" />}
                  {sched > 0 && <Pill label="scheduled" color="#ef4444" n={sched} onClick={focus(setShowDiscrepanciesSection)} testid="inline-status-month-scheduled" />}
                  {adj > 0 && <Pill label={`adjustment${adj === 1 ? '' : 's'}`} color="#0d9488" n={adj} onClick={focus(setShowAdjustmentsSection)} testid="inline-status-month-adjustment" />}
                </>
              );
            })()}
          </div>
          {sortedDates.length > 0 ? (
            <div className="space-y-6">
              {(() => {
                // Group dates by week number (1st of month = Week 1, next Monday = Week 2, etc.)
                const weekGroups = {};
                sortedDates.forEach(date => {
                  const dateObj = new Date(date);
                  const day = dateObj.getDate();
                  const firstOfMonth = new Date(dateObj.getFullYear(), dateObj.getMonth(), 1);
                  const firstDow = firstOfMonth.getDay(); // 0=Sun, 1=Mon...
                  // Days until the first Monday after the 1st (or 1st itself if Monday)
                  const daysToFirstMonday = firstDow === 0 ? 1 : (firstDow === 1 ? 0 : 8 - firstDow);
                  let weekNum;
                  if (day <= daysToFirstMonday || daysToFirstMonday === 0) {
                    weekNum = 1; // Before the first Monday (or first is Monday)
                  } else {
                    weekNum = Math.ceil((day - daysToFirstMonday) / 7) + 1;
                  }
                  if (!weekGroups[weekNum]) {
                    weekGroups[weekNum] = [];
                  }
                  weekGroups[weekNum].push(date);
                });
                
                return Object.entries(weekGroups).sort((a, b) => Number(b[0]) - Number(a[0])).map(([weekNum, dates]) => {
                  // Pre-compute groups across ALL month counts (not just same-date)
                  // so recounts created on different dates get grouped with their initial count
                  const allMonthCounts = filteredMonthCounts;
                  const globalUsed = new Set();
                  const groupsByCountId = {}; // map count.id -> group

                  // First pass: find all initial counts and attach their recounts from ANY date
                  allMonthCounts.filter(c => !(c.is_second_count || c.recount_number > 0)).forEach(count => {
                    if (globalUsed.has(count.id)) return;
                    const recounts = allMonthCounts.filter(c =>
                      c.id !== count.id &&
                      !globalUsed.has(c.id) &&
                      (c.original_count_id === count.id || c.parent_count_id === count.id)
                    );
                    globalUsed.add(count.id);
                    recounts.forEach(r => globalUsed.add(r.id));
                    const group = { initial: count, recounts };
                    groupsByCountId[count.id] = group;
                    recounts.forEach(r => { groupsByCountId[r.id] = group; });
                  });

                  // Second pass: orphan recounts (initial not in this month)
                  allMonthCounts.filter(c => (c.is_second_count || c.recount_number > 0) && !globalUsed.has(c.id)).forEach(count => {
                    globalUsed.add(count.id);
                    const rootId = count.original_count_id || count.id;
                    const siblings = allMonthCounts.filter(c =>
                      c.id !== count.id && !globalUsed.has(c.id) && c.original_count_id === rootId
                    );
                    siblings.forEach(s => globalUsed.add(s.id));
                    const group = { initial: count, recounts: siblings };
                    groupsByCountId[count.id] = group;
                    siblings.forEach(s => { groupsByCountId[s.id] = group; });
                  });

                  // Track which groups have already been rendered
                  const renderedGroups = new Set();

                  return (
                  <div key={weekNum} className="bg-gray-50 rounded-xl p-4">
                    <h3 className="text-base font-bold text-gray-500 mb-3 uppercase tracking-wide">
                      Week {weekNum}
                    </h3>
                    <div className="flex flex-wrap items-start gap-3">
                      {dates.map((date, dateIdx) => {
                        const dateCounts = countsByDate[date];
                        const dateObj = new Date(date);
                        const dayLabel = format(dateObj, 'EEE d MMM');
                        
                        return (
                          <React.Fragment key={date}>
                            {dateIdx > 0 && (
                              <div className="self-stretch w-px bg-gray-300 my-1" />
                            )}
                            <div className="flex flex-wrap items-start gap-3">
                              {(() => {
                                const result = [];
                                
                                dateCounts.forEach((count) => {
                                  const group = groupsByCountId[count.id];
                                  if (!group) return;
                                  // Use initial count id as unique group key
                                  const groupKey = group.initial.id;
                                  if (renderedGroups.has(groupKey)) return;
                                  renderedGroups.add(groupKey);

                                  const allCards = [group.initial, ...group.recounts];
                                  // Pick the latest count (highest recount_number)
                                  // — clicking the card on the list should
                                  // open the most recent count by default.
                                  const latest = allCards.reduce((acc, c) => {
                                    const accRn = acc.recount_number || (acc.is_second_count ? 1 : 0);
                                    const cRn = c.recount_number || (c.is_second_count ? 1 : 0);
                                    return cRn > accRn ? c : acc;
                                  }, allCards[0]);
                                  result.push(
                                    <div key={groupKey} className="w-[160px]">
                                      {renderCountCard(latest, 'month', { chainSize: allCards.length, rootId: groupKey })}
                                    </div>
                                  );
                                });
                                
                                return result;
                              })()}
                            </div>
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                  );
                });
              })()}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>No counts for {months[selectedMonth]} {currentYear}</p>
              <p className="text-sm mt-2">Create a count to get started</p>
            </div>
          )}
          </div>
        </div>
        </div>
      </div>
      <Dialog open={showUploadStockModal} onOpenChange={setShowUploadStockModal}>
      
      {/* Upload Stock Report Modal */}
        <DialogContent
          className="max-w-2xl v3-pg-dialog"
          style={{
            background: 'white',
            borderRadius: '12px',
            border: '3px solid',
            borderImage: 'linear-gradient(135deg, #ee7203, #f97316) 1'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Upload Stock Report
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Upload your GFST0000.csv file containing current stock data.
            </p>

            <button
              onClick={() => {
                navigator.clipboard.writeText('orders@c30dashboard.uk');
                setStockUploadEmailCopied(true);
                toast.success('Email address copied');
                setTimeout(() => setStockUploadEmailCopied(false), 2000);
              }}
              data-testid="pi-counts-copy-email"
              className="w-full flex items-center justify-between gap-2 px-4 py-2.5 rounded-xl border text-sm transition-colors hover:bg-orange-50"
              style={{ borderColor: `${enconOrange}55`, background: `${enconOrange}0d` }}
            >
              <span className="text-gray-600 text-left">Prefer email? Send it to your own @encon.co.uk email, then forward that to:</span>
              <span className="flex items-center gap-1.5 font-semibold whitespace-nowrap" style={{ color: enconOrange }}>
                orders@c30dashboard.uk
                {stockUploadEmailCopied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              </span>
            </button>

            {uploading ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Uploading...</span>
                  <span className="text-sm text-gray-500">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="h-3 rounded-full transition-all duration-300"
                    style={{
                      width: `${uploadProgress}%`,
                      background: 'linear-gradient(90deg, #ee7203, #f97316)'
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleStockUpload}
                  className="hidden"
                  id="stock-upload"
                />
                <label
                  htmlFor="stock-upload"
                  className="flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg cursor-pointer hover:bg-orange-50 transition-colors"
                  style={{ borderColor: enconOrange }}
                >
                  <Upload className="w-12 h-12 mb-3" style={{ color: enconOrange }} />
                  <p className="text-sm font-medium" style={{ color: enconOrange }}>
                    Click to upload CSV file
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    GFST0000 format
                  </p>
                </label>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Create Count Modal */}
      <Dialog open={showCreateCountModal} onOpenChange={setShowCreateCountModal}>
        <DialogContent
          className="rounded-2xl v3-pg-dialog"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Create Count
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {!shelfLifeMode && (
              <button
                type="button"
                onClick={loadShelfLifeProducts}
                disabled={loadingProducts}
                className="w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-dashed font-semibold transition-all hover:bg-red-50 disabled:opacity-50"
                style={{ borderColor: '#dc2626', color: '#dc2626' }}
                data-testid="shelf-life-products-btn"
              >
                <Calendar className="w-4 h-4" />
                {loadingProducts ? 'Loading Shelf Life products...' : 'Shelf Life Products'}
              </button>
            )}

            {shelfLifeMode && (
              <div className="p-3 rounded-lg border-2 bg-red-50" style={{ borderColor: '#dc2626' }} data-testid="shelf-life-banner">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" style={{ color: '#dc2626' }} />
                    <p className="text-sm font-semibold" style={{ color: '#dc2626' }}>Shelf Life Count — date-sensitive products only</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (shelfLifeAddTimeout) clearTimeout(shelfLifeAddTimeout);
                      setShelfLifeMode(false);
                      setSelectiveMode(false);
                      setSelectedProductCodes([]);
                      setAvailableProducts([]);
                      setShelfLifeAddQuery('');
                      setShelfLifeAddResults([]);
                      setNewCount(prev => ({ ...prev, type: 'structural' }));
                    }}
                    className="text-xs underline hover:text-red-800"
                    style={{ color: '#dc2626' }}
                    data-testid="shelf-life-back-btn"
                  >
                    ← Back to normal count
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Excludes self-seal and tape products. Enter the expiry date found on each pack once counted — the date is the main purpose of this count.
                </p>
              </div>
            )}

            {!shelfLifeMode && (
            <>
            <div>
              <Label className="text-sm font-medium mb-2 block">Count Type</Label>
              <div className="flex gap-3">
                <button
                  onClick={() => setNewCount({ ...newCount, type: 'structural' })}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newCount.type === 'structural'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-blue-300'
                  }`}
                >
                  <p className="font-semibold text-blue-600">Structural</p>
                </button>
                <button
                  onClick={() => setNewCount({ ...newCount, type: 'industrial' })}
                  className={`flex-1 p-3 border-2 rounded-lg transition-all ${
                    newCount.type === 'industrial'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <p className="font-semibold text-green-600">Industrial</p>
                </button>
              </div>
            </div>
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Supplier Groups</Label>
              
              {/* Search Bar for Supplier Groups */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={supplierSearchQuery}
                  onChange={(e) => setSupplierSearchQuery(e.target.value)}
                  placeholder="Search supplier groups..."
                  className="pl-10"
                />
              </div>
              
              {/* Multi-select Checkbox List */}
              <div className="border rounded-lg max-h-48 overflow-y-auto bg-gray-50">
                {filteredSuppliers.length > 0 ? (
                  filteredSuppliers.map((supplier) => (
                    <div
                      key={supplier.id}
                      onClick={() => toggleSupplierSelection(supplier.id)}
                      className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-orange-50 border-b last:border-b-0 transition-colors ${
                        newCount.supplier_group_ids.includes(supplier.id) ? 'bg-orange-50' : ''
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          newCount.supplier_group_ids.includes(supplier.id)
                            ? 'border-orange-500 bg-orange-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {newCount.supplier_group_ids.includes(supplier.id) && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{supplier.name}</p>
                        {supplier.products_in_stock && (
                          <p className="text-xs text-gray-500">{supplier.products_in_stock} products in stock</p>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {supplierSearchQuery 
                      ? `No suppliers matching "${supplierSearchQuery}"` 
                      : 'No suppliers available in this category'}
                  </div>
                )}
              </div>
              
              {/* Selected Count Badge */}
              {newCount.supplier_group_ids.length > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <span 
                    className="px-2 py-1 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: enconOrange }}
                  >
                    {newCount.supplier_group_ids.length} selected
                  </span>
                  <button
                    onClick={() => setNewCount({ ...newCount, supplier_group_ids: [] })}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>
            </>
            )}
            
            {/* Choose Select Products Button */}
            {newCount.supplier_group_ids.length > 0 && !selectiveMode && (
              <Button
                onClick={loadProductsForSelection}
                variant="outline"
                className="w-full border-2 border-dashed"
                style={{ borderColor: enconOrange, color: enconOrange }}
                disabled={loadingProducts}
                data-testid="choose-select-products-btn"
              >
                {loadingProducts ? 'Loading products...' : 'Choose Select Products for Count'}
              </Button>
            )}

            {/* Product Selection Panel */}
            {selectiveMode && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">{shelfLifeMode ? 'Shelf Life Products' : 'Select Products'}</Label>
                  {!shelfLifeMode && (
                    <button
                      onClick={() => { setSelectiveMode(false); setSelectedProductCodes([]); setAvailableProducts([]); }}
                      className="text-xs underline hover:text-gray-700"
                      style={{ color: enconOrange }}
                    >
                      Count all products instead
                    </button>
                  )}
                </div>

                {shelfLifeMode && (
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={shelfLifeAddQuery}
                      onChange={(e) => handleShelfLifeAddQueryChange(e.target.value)}
                      placeholder="Add a product not on the list (search code or name)..."
                      className="pl-10"
                      data-testid="shelf-life-add-input"
                    />
                    {shelfLifeAddResults.length > 0 && (
                      <div className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="shelf-life-add-results">
                        {shelfLifeAddResults.map((product, idx) => (
                          <button
                            key={product.code || product.product_code || idx}
                            onClick={() => addShelfLifeProduct(product)}
                            className="w-full text-left px-3 py-2 hover:bg-red-50 border-b last:border-b-0 flex items-center justify-between gap-2"
                            data-testid={`shelf-life-add-result-${idx}`}
                          >
                            <span className="text-sm font-mono text-gray-600">{product.code || product.product_code}</span>
                            <span className="text-sm text-gray-800 truncate flex-1 text-right">{product.name || product.description || product.product_name || ''}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Product Search */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="pl-10"
                    data-testid="product-search-input"
                  />
                </div>

                {/* Select All / Clear All */}
                <div className="flex gap-2 mb-2">
                  <button
                    onClick={() => setSelectedProductCodes(availableProducts.map(p => p.product_code))}
                    className="text-xs font-medium px-2 py-1 rounded hover:bg-orange-50"
                    style={{ color: enconOrange }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => {
                      // Select only products IN STOCK
                      const inStockProducts = availableProducts.filter(p => !p.not_in_stock);
                      setSelectedProductCodes(inStockProducts.map(p => p.product_code));
                    }}
                    className="text-xs font-medium px-2 py-1 rounded hover:bg-green-50 text-green-600"
                  >
                    Select In Stock Only
                  </button>
                  <button
                    onClick={() => setSelectedProductCodes([])}
                    className="text-xs font-medium text-gray-500 px-2 py-1 rounded hover:bg-gray-100"
                  >
                    Clear All
                  </button>
                  <span className="text-xs text-gray-400 ml-auto self-center">
                    {selectedProductCodes.length}/{availableProducts.length} selected
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  <span className="text-red-500 font-bold">*</span> = Not in stock (will show as unavailable)
                </p>

                {/* Product List */}
                <div className="border rounded-lg max-h-48 overflow-y-auto bg-gray-50">
                  {availableProducts
                    .filter(p => {
                      if (!productSearchQuery) return true;
                      const q = productSearchQuery.toLowerCase();
                      return (p.product_code || '').toLowerCase().includes(q) || 
                             (p.product_name || '').toLowerCase().includes(q);
                    })
                    .map((product) => (
                    <div
                      key={product.product_code}
                      onClick={() => toggleProductSelection(product.product_code)}
                      className={`flex items-center gap-3 p-2.5 cursor-pointer hover:bg-orange-50 border-b last:border-b-0 transition-colors ${
                        selectedProductCodes.includes(product.product_code) ? 'bg-orange-50' : ''
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          selectedProductCodes.includes(product.product_code)
                            ? 'border-orange-500 bg-orange-500'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {selectedProductCodes.includes(product.product_code) && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-xs text-gray-600">
                          {product.product_code}
                          {product.not_in_stock && <span className="text-red-500 font-bold ml-1">*</span>}
                        </p>
                        <p className="text-sm font-medium truncate">{product.product_name || 'No description'}</p>
                      </div>
                    </div>
                  ))}
                  {availableProducts.length === 0 && (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No products found in selected supplier groups
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-sm font-medium mb-2 block">Count Date</Label>
              <Input
                type="date"
                value={newCount.date}
                onChange={(e) => setNewCount({ ...newCount, date: e.target.value })}
              />
            </div>
            
            <Button
              onClick={shelfLifeMode ? createShelfLifeCount : createCount}
              className="w-full"
              style={{ backgroundColor: shelfLifeMode ? '#dc2626' : enconOrange, color: 'white' }}
              data-testid="create-count-submit-btn"
            >
              {shelfLifeMode
                ? `Create Shelf Life Count (${selectedProductCodes.length} products)`
                : (selectiveMode ? `Create Count Sheet (${selectedProductCodes.length} products)` : 'Create Count Sheet')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Inventory Search Modal */}
      <Dialog open={showInventoryModal} onOpenChange={(open) => {
        setShowInventoryModal(open);
        if (!open) {
          setSelectedProduct(null);
          setInventorySearchQuery('');
          setInventoryResults([]);
        }
      }}>
        <DialogContent
          className="max-w-4xl max-h-[80vh] rounded-2xl overflow-hidden v3-pg-dialog"
          style={{
            background: 'white',
            borderRadius: '16px',
            border: '3px solid transparent',
            backgroundImage: 'linear-gradient(white, white), linear-gradient(135deg, #ee7203, #ec4899)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box'
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold" style={{ color: enconOrange }}>
              Stock on Hand - Inventory Search
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex gap-4 h-[calc(80vh-120px)]">
            {/* Left side - Search and Results */}
            <div className={`flex flex-col space-y-4 transition-all duration-300 ${selectedProduct ? 'w-1/2' : 'w-full'}`}>
              <div className="flex gap-2">
                <Input
                  value={inventorySearchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search by product code, name, or prefix..."
                  className="flex-1"
                />
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                {Array.isArray(inventoryResults) && inventoryResults.length > 0 ? (
                  inventoryResults.map((product) => {
                    // Defensive check: ensure product is an object
                    if (!product || typeof product !== 'object') return null;
                    const productCode = String(product.code || product.product_code || 'Unknown');
                    const productName = String(product.name || product.description || product.product_name || '');
                    const productPrefix = String(product.prefix || '');
                    const productId = product.id || `product-${Math.random()}`;
                    
                    return (
                      <div
                        key={productId}
                        onClick={() => handleProductClick(product)}
                        className={`p-3 border-2 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer ${
                          selectedProduct?.id === product.id ? 'border-orange-500 bg-orange-50' : ''
                        }`}
                        style={{ borderColor: selectedProduct?.id === product.id ? enconOrange : enconLightGrey }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-lg">
                              {productCode}
                              {product.not_in_stock && <span className="text-red-500 font-bold ml-1">*</span>}
                            </p>
                            <p className="text-sm text-gray-600">{productName || <span className="text-gray-400 italic">No description</span>}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              Group: {productPrefix}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : inventorySearchQuery ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No products found matching &quot;{inventorySearchQuery}&quot;</p>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Start typing to search products...</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Right side - Product Details (slides in) */}
            {selectedProduct && (
              <div className="w-1/2 border-l-2 pl-4 overflow-y-auto animate-in slide-in-from-right duration-300">
                <div className="space-y-4">
                  <div className="pb-3 border-b-2" style={{ borderColor: enconOrange }}>
                    <h3 className="text-xl font-bold" style={{ color: enconOrange }}>
                      Product Details
                    </h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-xs font-semibold text-gray-500 uppercase">Product Code</label>
                      <p className="text-lg font-bold mt-1">{selectedProduct.code || selectedProduct.product_code}</p>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-xs font-semibold text-gray-500 uppercase">Description</label>
                      <p className="text-sm mt-1">{selectedProduct.name || selectedProduct.description || selectedProduct.product_name || <span className="text-gray-400 italic">No description</span>}</p>
                    </div>
                    
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-xs font-semibold text-gray-500 uppercase">Product Group</label>
                      <p className="text-sm mt-1">{selectedProduct.prefix}</p>
                    </div>
                    
                    {/* On Hand Quantity - Primary metric */}
                    <div className="p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                      <label className="text-xs font-semibold text-blue-700 uppercase">On Hand Qty</label>
                      <div className="flex items-baseline gap-2 mt-1">
                        <p className="text-2xl font-bold text-blue-700">
                          {(selectedProduct.on_hand_quantity ?? selectedProduct.on_hand_balance ?? selectedProduct.free_stock)?.toLocaleString() || '0'}
                        </p>
                        <p className="text-sm text-blue-600">
                          {selectedProduct.stock_unit || 'units'}
                        </p>
                      </div>
                    </div>
                    
                    {/* On Hand & Allocated */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <label className="text-xs font-semibold text-gray-500 uppercase">On Hand</label>
                        <p className="text-lg font-bold mt-1">
                          {selectedProduct.on_hand_quantity?.toLocaleString() || '0'}
                        </p>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <label className="text-xs font-semibold text-gray-500 uppercase">Allocated</label>
                        <p className="text-lg font-bold mt-1">
                          {selectedProduct.allocated_stock?.toLocaleString() || '0'}
                        </p>
                      </div>
                    </div>
                    
                    {/* On Order */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="text-xs font-semibold text-gray-500 uppercase">On Order</label>
                      <p className="text-lg font-bold mt-1">
                        {selectedProduct.on_order_quantity?.toLocaleString() || '0'}
                      </p>
                    </div>
                    
                    {/* Stock Value & Cost */}
                    <div className="p-3 bg-green-50 rounded-lg border-2 border-green-200">
                      <label className="text-xs font-semibold text-green-700 uppercase">Stock Value</label>
                      <div className="space-y-1 mt-1">
                        <div className="flex items-baseline gap-2">
                          <p className="text-2xl font-bold text-green-700">
                            £{selectedProduct.stock_value?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                          </p>
                        </div>
                        {selectedProduct.average_cost > 0 && (
                          <p className="text-xs text-green-600">
                            Avg Cost: £{selectedProduct.average_cost?.toFixed(5)} per {selectedProduct.costing_unit || 'unit'}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {selectedProduct.supplier_group && (
                      <div className="p-3 bg-orange-50 rounded-lg border-2 border-orange-200">
                        <label className="text-xs font-semibold text-orange-700 uppercase">Supplier Group</label>
                        <p className="text-sm font-semibold mt-1" style={{ color: enconOrange }}>
                          {selectedProduct.supplier_group}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to Warehouse Modal (from Quick Menu) */}
      <Dialog open={showAssignModal} onOpenChange={(open) => { if (!open) setShowAssignModal(false); }}>
        <DialogContent className="max-w-md v3-pg-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold" style={{ color: enconOrange }}>Send to Warehouse</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Assign count to
              </label>
              
              {/* Team filter tabs */}
              <div className="flex gap-1 mb-3 flex-wrap">
                {[
                  { value: 'default', label: 'W/H & Ops' },
                  { value: 'warehouse', label: 'Warehouse' },
                  { value: 'operations', label: 'Operations' },
                  { value: 'office_sales', label: 'Office Sales' },
                  { value: 'admin', label: 'Admin' },
                  { value: 'all', label: 'All' }
                ].map(tab => (
                  <button
                    key={tab.value}
                    onClick={() => setSelectedTeamFilter(tab.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                      selectedTeamFilter === tab.value
                        ? 'text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    style={selectedTeamFilter === tab.value ? { backgroundColor: enconOrange } : {}}
                    data-testid={`team-filter-${tab.value}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              
              {/* Team member list */}
              <div className="border rounded-lg max-h-48 overflow-y-auto" data-testid="team-member-list">
                {getFilteredTeamMembers().length > 0 ? (
                  getFilteredTeamMembers().map(member => (
                    <button
                      key={member.id}
                      onClick={() => {
                        setAssigneeName(member.name);
                        setAssigneeEmail(member.email);
                      }}
                      className={`w-full text-left px-3 py-2.5 flex items-center justify-between border-b last:border-b-0 transition-colors ${
                        assigneeEmail === member.email ? 'bg-orange-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`team-member-${member.id}`}
                    >
                      <div>
                        <span className={`text-sm font-medium ${assigneeEmail === member.email ? 'text-orange-700' : 'text-gray-800'}`}>
                          {member.name}
                        </span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
                          member.team === 'warehouse' ? 'bg-amber-100 text-amber-700' :
                          member.team === 'operations' ? 'bg-teal-100 text-teal-700' :
                          member.team === 'office_sales' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {member.team === 'office_sales' ? 'Office Sales' : 
                           member.team.charAt(0).toUpperCase() + member.team.slice(1)}
                        </span>
                      </div>
                      {assigneeEmail === member.email && (
                        <Check className="w-4 h-4 text-orange-600" />
                      )}
                    </button>
                  ))
                ) : (
                  <div className="px-3 py-4 text-center text-sm text-gray-500">
                    No team members found for this branch. Set up teams in Settings &gt; User Admin.
                  </div>
                )}
              </div>
              
              {assigneeName && (
                <p className="text-xs mt-2 font-medium" style={{ color: enconOrange }}>
                  Selected: {assigneeName}
                </p>
              )}
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <Button
                data-testid="cancel-assign-btn"
                variant="outline"
                onClick={() => setShowAssignModal(false)}
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-send-warehouse-btn"
                onClick={confirmSendToWarehouse}
                disabled={sendingToWarehouse}
                style={{ backgroundColor: enconOrange, color: 'white' }}
              >
                <Send className="w-4 h-4 mr-2" />
                {sendingToWarehouse ? 'Sending...' : 'Send to W/H'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* New Accounts Modal */}
      {showNewAccountsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={() => setShowNewAccountsModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: '95vw', height: '92vh' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ backgroundColor: enconOrange }}>
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-white" />
                <span className="font-bold text-white text-lg">New Accounts</span>
              </div>
              <button onClick={() => setShowNewAccountsModal(false)} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors" data-testid="close-new-accounts-modal">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <iframe src="https://forms.office.com/pages/responsepage.aspx?id=PlJxjHhvUUWoFOzwm7bCYumXt2cxmMBAtzfRqtGCOwxUREFWSExLOFBWT1o3QTgyQ0xDVjRaR01VQi4u&route=shorturl" className="flex-1 w-full border-0 rounded-b-xl" title="New Accounts Form" allow="clipboard-write" />
          </div>
        </div>
      )}
      <CreateReportModal open={showCreateReportModal} onClose={() => setShowCreateReportModal(false)} />
      <SavedReportsModal open={showSavedReportsModal} onClose={() => setShowSavedReportsModal(false)} />

      {/* Completed Adjustments — full grouped list (opened from compact card). */}
      <Dialog open={showCompletedAdjustmentsModal} onOpenChange={setShowCompletedAdjustmentsModal}>
        <DialogContent className="max-w-2xl rounded-2xl v3-pg-dialog" data-testid="completed-adjustments-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              Completed Adjustments — {(() => { try { return new Date(currentYear, selectedMonth, 1).toLocaleString(undefined,{month:'long', year:'numeric'}); } catch { return ''; } })()}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto pr-1">
            {(() => {
              // Restrict the modal list to the SAME month/year the user is
              // viewing on the page (mirrors the count shown on the green
              // "Completed" stat card). Without this every month's adjustments
              // showed in one big list, defeating the per-month "Click for
              // details" pattern.
              const list = (completedAdjustments || []).filter(a => {
                const ts = a.completed_at || a.updated_at || a.created_at || '';
                if (!ts) return false;
                const d = new Date(ts);
                return !isNaN(d) && d.getFullYear() === currentYear && d.getMonth() === selectedMonth;
              });
              if (list.length === 0) {
                return <p className="text-sm text-gray-500 italic text-center py-8">No completed adjustments in this month yet.</p>;
              }
              const groups = new Map();
              list.forEach(a => {
                const ts = a.completed_at || a.updated_at || a.created_at || '';
                const dayIso = ts ? ts.slice(0, 10) : 'unknown';
                if (!groups.has(dayIso)) groups.set(dayIso, []);
                groups.get(dayIso).push(a);
              });
              const sortedDays = Array.from(groups.keys()).sort((a, b) => b.localeCompare(a));
              const fmtDay = (iso) => {
                if (!iso || iso === 'unknown') return 'Unknown date';
                try {
                  const d = new Date(iso);
                  if (isNaN(d)) return iso;
                  const day = d.getDate();
                  const suf = (day >= 11 && day <= 13) ? 'th' : ({1:'st',2:'nd',3:'rd'}[day%10] || 'th');
                  return `${d.toLocaleDateString(undefined,{weekday:'long'})} ${day}${suf} ${d.toLocaleDateString(undefined,{month:'long'})} ${d.getFullYear()}`;
                } catch { return iso; }
              };
              return (
                <div className="space-y-4">
                  {sortedDays.map(day => (
                    <div key={day}>
                      <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 border-b border-emerald-100 pb-1 mb-2">
                        {fmtDay(day)}  ·  {groups.get(day).length} adjustment{groups.get(day).length === 1 ? '' : 's'}
                      </p>
                      <ul className="divide-y">
                        {groups.get(day).map(a => {
                          const items = a.items?.length || a.products?.length || 0;
                          return (
                            <li
                              key={a.id}
                              onClick={() => { setShowCompletedAdjustmentsModal(false); navigate(`/pi-counts/adjustment/${a.id}`); }}
                              className="flex items-center justify-between gap-2 px-2 py-2 rounded-lg hover:bg-emerald-50 cursor-pointer transition-colors"
                              data-testid={`completed-adj-modal-row-${a.id}`}
                            >
                              <span className="font-semibold text-gray-800">{a.count_name || a.supplier_name || 'Adjustment'}</span>
                              <span className="text-xs text-emerald-700 font-bold">{items} item{items === 1 ? '' : 's'}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PICounts;
